import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Grid, MenuItem, Paper, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Typography
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useParams } from 'react-router-dom';
import { getRentalInvoiceById, payRentalInvoice } from '../../services/api';
import { getErrorMessage } from '../../utils/errorMessage';
import { printRentalInvoice } from '../../utils/rentalInvoicePrint';
import { cycleTitle } from '../../utils/rentalBilling';

const money = (v) =>
  parseFloat(v || 0).toLocaleString('en-LK', { style: 'currency', currency: 'LKR' });

const statusColor = (status) => {
  const map = { pending: 'warning', partial: 'info', overdue: 'error', paid: 'success', cancelled: 'default' };
  return map[status] || 'default';
};

const RentalInvoiceView = () => {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [payOpen, setPayOpen] = useState(false);
  const [payAmount, setPayAmount] = useState('');
  const [payMethod, setPayMethod] = useState('Cash');
  const [payNotes, setPayNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await getRentalInvoiceById(invoiceId);
      setInv(res.data);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load invoice'));
      setInv(null);
    } finally {
      setLoading(false);
    }
  }, [invoiceId]);

  useEffect(() => { load(); }, [load]);

  const savePay = async () => {
    if (!inv) return;
    try {
      setBusy(true);
      await payRentalInvoice(inv.id, {
        amount: parseFloat(payAmount),
        payment_method: payMethod,
        payment_notes: payNotes
      });
      setPayOpen(false);
      await load();
    } catch (err) {
      setError(getErrorMessage(err, 'Payment failed'));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <CircularProgress />;
  if (error && !inv) return <Alert severity="error">{error}</Alert>;

  const openPay = ['pending', 'partial', 'overdue'].includes(inv.display_status);

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/rentals/invoices')} sx={{ mb: 2 }}>
        All invoices
      </Button>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>{inv.invoice_number}</Typography>
          <Typography variant="body2" color="text.secondary">
            {inv.customer?.name}
            {inv.customer?.phone ? ` · ${inv.customer.phone}` : ''}
            {' · '}
            <Button size="small" onClick={() => navigate(`/rentals/${inv.rental_id}`)}>
              {inv.rental_number}
            </Button>
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Chip label={inv.display_status} color={statusColor(inv.display_status)} />
          <Button variant="contained" onClick={() => printRentalInvoice(inv)}>Print invoice</Button>
          {openPay && (
            <Button
              onClick={() => {
                setPayAmount(String(inv.balance || inv.amount));
                setPayOpen(true);
              }}
            >
              Record payment
            </Button>
          )}
        </Box>
      </Box>

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        {[
          { label: 'Period', value: `${inv.period_start} → ${inv.period_end}` },
          { label: 'Due', value: inv.due_date },
          { label: 'Amount', value: money(inv.amount) },
          { label: 'Paid', value: money(inv.paid_amount) },
          { label: 'Balance', value: money(inv.balance) },
          { label: 'Billing', value: cycleTitle(inv.billing_cycle) }
        ].map((card) => (
          <Grid item xs={6} md={2} key={card.label}>
            <Card variant="outlined">
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="caption" color="text.secondary">{card.label}</Typography>
                <Typography variant="subtitle1" fontWeight={700}>{card.value}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {inv.payment_notes ? (
        <Alert severity="info" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
          {inv.payment_notes}
        </Alert>
      ) : null}

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>Rental items</Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Product</TableCell>
              <TableCell>Serial</TableCell>
              <TableCell align="right">Period rate</TableCell>
              <TableCell align="right">Qty</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(inv.items || []).map((it, idx) => (
              <TableRow key={`${it.serial_number || it.product_name}-${idx}`}>
                <TableCell>{it.product_name}</TableCell>
                <TableCell>{(it.serials || []).join(', ') || it.serial_number || '—'}</TableCell>
                <TableCell align="right">{money(it.period_rate)}</TableCell>
                <TableCell align="right">{it.quantity || 1}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={payOpen} onClose={() => setPayOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Record payment</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {inv.invoice_number} · Balance {money(inv.balance)}
          </Typography>
          <TextField
            fullWidth
            type="number"
            label="Amount"
            value={payAmount}
            onChange={(e) => setPayAmount(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            select
            fullWidth
            label="Method"
            value={payMethod}
            onChange={(e) => setPayMethod(e.target.value)}
            sx={{ mb: 2 }}
          >
            {['Cash', 'Bank Transfer', 'Card', 'Cheque', 'Other'].map((m) => (
              <MenuItem key={m} value={m}>{m}</MenuItem>
            ))}
          </TextField>
          <TextField fullWidth label="Notes" value={payNotes} onChange={(e) => setPayNotes(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={savePay} disabled={busy}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RentalInvoiceView;
